import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Segmented,
  Space,
  Tag,
  Typography,
} from "antd";
import { useUiState } from "../state";
import { textMap } from "../i18n";
import {
  fetchBillingSummary,
  fetchPlans,
  type BillingSummary,
  type PlanListItem,
} from "../services/compliance";

const { Title, Text, Paragraph } = Typography;

function AnimatedNumber({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const start = display;
    const end = value;
    const duration = 380;
    let frame = 0;
    let startTime = 0;

    const tick = (ts: number) => {
      if (!startTime) {
        startTime = ts;
      }
      const p = Math.min(1, (ts - startTime) / duration);
      const eased = 1 - (1 - p) * (1 - p);
      const current = Math.round(start + (end - start) * eased);
      setDisplay(current);
      if (p < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return <>{display}</>;
}

type TierKey = "free" | "personal" | "pro" | "enterprise";

/** 与后端 /plans 返回的 code、name、价格对齐；避免「Free Trial」因不含 pro 被误判为 Personal。 */
function tierFromApiPlan(p: PlanListItem): TierKey {
  const c = p.code.toLowerCase();
  const n = p.name.toLowerCase();
  if (c.includes("enterprise")) return "enterprise";
  if (c.includes("pro")) return "pro";
  if (c.includes("personal")) return "personal";
  if (
    c.includes("free") ||
    c.includes("trial") ||
    n.includes("free") ||
    n.includes("trial")
  ) {
    return "free";
  }
  if (p.price_usd_cents === 0) return "free";
  return "personal";
}

function getTierFromPlan(summary: BillingSummary | null): TierKey {
  const code = (summary?.plan?.code ?? "").toUpperCase();
  const name = (summary?.plan?.name ?? "").toUpperCase();
  const source = `${code} ${name}`;
  if (source.includes("ENTERPRISE")) return "enterprise";
  if (source.includes("PRO")) return "pro";
  if (source.includes("PERSONAL")) return "personal";
  return "free";
}

export function UpgradePlanPage() {
  const { lang } = useUiState();
  const t = textMap[lang];
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [apiPlans, setApiPlans] = useState<PlanListItem[]>([]);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [selectedPlanKey, setSelectedPlanKey] = useState<string>("pro");

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchBillingSummary(), fetchPlans()]).then(([billing, planRows]) => {
      if (!mounted) return;
      setSummary(billing);
      setApiPlans(planRows);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const currentTier = useMemo(() => getTierFromPlan(summary), [summary]);

  const plans = useMemo(() => {
    const filtered = apiPlans.filter((p) => p.billing_cycle === billingCycle);
    const tierOrder: TierKey[] = ["personal", "pro", "enterprise"];
    const featureMap: Record<TierKey, string[]> = {
      free: [lang === "zh" ? "5 次/月" : "5 reviews/mo", lang === "zh" ? "基础建议" : "Basic suggestions", lang === "zh" ? "社区支持" : "Community support"],
      personal: t.personalFeatures,
      pro: t.proFeatures,
      enterprise: t.enterpriseFeatures,
    };
    const paidPlans = filtered
      .filter((p) => tierFromApiPlan(p) !== "free")
      .sort((a, b) => {
        const ta = tierFromApiPlan(a);
        const tb = tierFromApiPlan(b);
        const ai = tierOrder.indexOf(ta);
        const bi = tierOrder.indexOf(tb);
        return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
      })
      .map((p) => {
        const tier = tierFromApiPlan(p);
        const price = p.price_usd_cents / 100;
        return {
          key: p.code,
          tier,
          name: p.name,
          monthlyPrice: billingCycle === "monthly" ? price : 0,
          yearlyPrice: billingCycle === "yearly" ? price : 0,
          monthlyScans: billingCycle === "monthly" ? p.scan_quota : null,
          yearlyScans: billingCycle === "yearly" ? p.scan_quota : null,
          features: featureMap[tier] ?? [],
          highlight: tier === "pro",
        };
      });
    const freePlan = {
      key: "free" as const,
      tier: "free" as TierKey,
      name: lang === "zh" ? "Free（基础免费）" : "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      monthlyScans: 5,
      yearlyScans: 60,
      features: featureMap.free,
      highlight: false,
    };
    return [freePlan, ...paidPlans];
  }, [apiPlans, billingCycle, lang, t.enterpriseFeatures, t.personalFeatures, t.proFeatures]);

  useEffect(() => {
    if (!plans.length) return;
    const exists = plans.some((plan) => plan.key === selectedPlanKey);
    if (!exists) setSelectedPlanKey(plans[0].key);
  }, [selectedPlanKey, plans]);

  const showUpgradeBlocked = () => {
    Modal.info({
      title: t.upgradeBetaModalTitle,
      content: t.upgradeBetaModalBody,
      okText: t.upgradeBetaModalOk,
      centered: true,
    });
  };

  const activePlanName =
    summary?.plan?.name ?? (lang === "zh" ? "Free（未开通）" : "Free (No active plan)");
  const renewAtLabel = summary?.renew_at
    ? new Date(summary.renew_at).toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US")
    : "-";

  return (
    <div className="upgrade-sentinel">
      <div className="upgrade-current-banner">
        <Text strong>
          {lang === "zh" ? "当前套餐：" : "Current Plan: "}
          {activePlanName}
        </Text>
        <Text type="secondary">
          {lang === "zh" ? "续费时间：" : "Renewal: "}
          {renewAtLabel}
        </Text>
      </div>

      <header className="upgrade-hero">
        <Title level={2} className="upgrade-title">
          {lang === "zh" ? "套餐升级" : "Upgrade Plan"}
        </Title>
        <Paragraph className="upgrade-subtitle">
          {lang === "zh"
            ? "以下为全部套餐定价，可在月付与年付之间切换查看。"
            : "All plans and pricing below. Switch between monthly and yearly billing."}
        </Paragraph>
      </header>

      <Card
        className="upgrade-pricing-shell"
        title={t.upgradePricingCardTitle}
        bordered={false}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert type="info" showIcon message={t.upgradeBetaBanner} />
          <Space
            align="center"
            className="upgrade-cycle-row"
            style={{ justifyContent: "space-between", width: "100%" }}
          >
            <Text type="secondary">{t.pricingInWorkspaceDesc}</Text>
            <div className="upgrade-cycle-controls">
              <Tag color="cyan">{t.planSaveHint}</Tag>
              <Segmented
                value={billingCycle}
                options={[
                  { label: t.planMonthlyLabel, value: "monthly" },
                  { label: t.planYearlyLabel, value: "yearly" },
                ]}
                onChange={(value) => setBillingCycle(value as "monthly" | "yearly")}
              />
            </div>
          </Space>
        {plans.length === 0 ? (
          <Empty
            description={
              lang === "zh" ? "暂时无法加载定价，请稍后重试。" : "Could not load pricing. Try again later."
            }
          />
        ) : (
          <Row gutter={[16, 16]}>
            {plans.map((plan) => (
              <Col xs={24} sm={12} xl={6} key={plan.key}>
                <Card
                  className={`upgrade-plan-card ${"highlight" in plan && plan.highlight ? "is-highlight" : ""} ${
                    selectedPlanKey === plan.key ? "tier-card-selected" : ""
                  } ${plan.tier === currentTier ? "is-current-tier" : ""}`}
                  bordered={false}
                  onClick={() => setSelectedPlanKey(plan.key)}
                >
                  <Tag
                    color={billingCycle === "yearly" ? "magenta" : "default"}
                    className="tier-save-tag"
                  >
                    {(() => {
                      if (plan.tier === "free") {
                        return lang === "zh" ? "免费" : "Free";
                      }
                      if (billingCycle === "monthly") {
                        return lang === "zh" ? "月付标准价" : "Monthly standard price";
                      }
                      const yearlyBase = plan.monthlyPrice * 12;
                      if (yearlyBase <= 0) {
                        return lang === "zh" ? "年付" : "Yearly";
                      }
                      const saved = Math.max(0, yearlyBase - plan.yearlyPrice);
                      const pct = Math.round((saved / yearlyBase) * 100);
                      return lang === "zh"
                        ? `年付省 ${pct}% (-$${saved})`
                        : `Yearly save ${pct}% (-$${saved})`;
                    })()}
                  </Tag>
                  {plan.tier === currentTier &&
                  !(plan.tier === "free" && currentTier === "free") ? (
                    <Tag color="green" style={{ marginBottom: 8 }}>
                      {t.currentPlanTag}
                    </Tag>
                  ) : null}
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Text strong>{plan.name}</Text>
                    <div className="billing-price-row">
                      <Title level={3} style={{ margin: 0 }}>
                        $
                        <AnimatedNumber
                          value={billingCycle === "monthly" ? plan.monthlyPrice : plan.yearlyPrice}
                        />
                      </Title>
                      <Text type="secondary">
                        {billingCycle === "monthly"
                          ? lang === "zh"
                            ? "/月"
                            : "/month"
                          : lang === "zh"
                            ? "/年"
                            : "/year"}
                      </Text>
                    </div>
                    <Text>
                      {plan.monthlyScans === null
                        ? lang === "zh"
                          ? "不限扫描"
                          : "Unlimited scans"
                        : billingCycle === "monthly"
                          ? lang === "zh"
                            ? `${plan.monthlyScans} 次扫描`
                            : `${plan.monthlyScans} scans`
                          : lang === "zh"
                            ? `${plan.yearlyScans} 次扫描`
                            : `${plan.yearlyScans} scans`}
                    </Text>
                    {plan.features.map((f) => (
                      <Text key={f} type="secondary">
                        {f}
                      </Text>
                    ))}
                    {plan.tier === "free" ? (
                      currentTier === "free" ? (
                        <Button type="default" block disabled>
                          {t.upgradeFreeYouAreOn}
                        </Button>
                      ) : (
                        <Text type="secondary" style={{ display: "block", textAlign: "center" }}>
                          {t.upgradeFreeTierCaption}
                        </Text>
                      )
                    ) : (
                      <Button
                        type={plan.tier === currentTier ? "default" : "primary"}
                        block
                        disabled={plan.tier === currentTier}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (plan.tier === currentTier) return;
                          showUpgradeBlocked();
                        }}
                      >
                        {plan.tier === currentTier
                          ? t.currentPlanTag
                          : lang === "zh"
                            ? `升级到 ${plan.name}`
                            : `Upgrade to ${plan.name}`}
                      </Button>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        </Space>
      </Card>

      <Card
        className="upgrade-compare-card"
        title={lang === "zh" ? "功能对比" : "Feature Comparison"}
        bordered={false}
      >
        <div className="upgrade-compare-table">
          <div className="upgrade-compare-head">
            <span>{lang === "zh" ? "能力项" : "Capabilities"}</span>
            <span>{lang === "zh" ? "Free" : "Free"}</span>
            <span>{t.personalPlanName}</span>
            <span>{t.proPlanName}</span>
            <span>{t.enterprisePlanName}</span>
          </div>

          <div className="upgrade-compare-row">
            <span>{lang === "zh" ? "每月审查次数" : "Monthly review limit"}</span>
            <span>5</span>
            <span>30</span>
            <span>70</span>
            <span>{lang === "zh" ? "不限" : "Unlimited"}</span>
          </div>

          <div className="upgrade-compare-row">
            <span>{t.enterpriseIncludesProRow}</span>
            <span>-</span>
            <span>-</span>
            <span>-</span>
            <span>✓</span>
          </div>

          <div className="upgrade-compare-row">
            <span>{lang === "zh" ? "AI 一键改写" : "AI one-click rewrite"}</span>
            <span>-</span>
            <span>-</span>
            <span>✓</span>
            <span>✓</span>
          </div>

          <div className="upgrade-compare-row">
            <span>
              {lang === "zh"
                ? "FDA / FTC 公开数据证据提示"
                : "FDA / FTC public evidence signals"}
            </span>
            <span>-</span>
            <span>-</span>
            <span>✓</span>
            <span>✓</span>
          </div>

          <div className="upgrade-compare-row">
            <span>
              {lang === "zh"
                ? "专业工作台 Studio（扫描、记录、法规监控等）"
                : "Studio workbench (scans, history, monitoring)"}
            </span>
            <span>-</span>
            <span>-</span>
            <span>✓</span>
            <span>✓</span>
          </div>

          <div className="upgrade-compare-row">
            <span>{lang === "zh" ? "团队协作与权限" : "Team controls"}</span>
            <span>-</span>
            <span>-</span>
            <span>-</span>
            <span>✓</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

